import { shallowRef, unref, watch } from 'vue';

import { keyNames } from '@components/utils';

import type { CdsList } from '../components/CdsList';
import type { CdsTextInput } from '../components/CdsTextInput';
import type { Ref } from 'vue';

export function useScrolling(
  listRef: Ref<typeof CdsList | undefined>,
  textFieldRef: Ref<typeof CdsTextInput | undefined>
) {
  const isScrolling = shallowRef(false);

  let scrollTimeout: number;
  function onListScroll(event: Event) {
    cancelAnimationFrame(scrollTimeout);
    isScrolling.value = true;
    scrollTimeout = requestAnimationFrame(() => {
      scrollTimeout = requestAnimationFrame(() => {
        isScrolling.value = false;
      });
    });
  }

  async function finishScrolling() {
    await new Promise((resolve) => requestAnimationFrame(resolve));
    await new Promise((resolve) => requestAnimationFrame(resolve));
    await new Promise((resolve) => requestAnimationFrame(resolve));
    await new Promise<void>((resolve) => {
      if (unref(isScrolling)) {
        const stop = watch(isScrolling, () => {
          stop();
          resolve();
        });
      } else resolve();
    });
  }

  async function onListKeydown(event: KeyboardEvent) {
    if (event.key === keyNames.tab) {
      unref(textFieldRef)?.focus();
    }

    if (![keyNames.pagedown, keyNames.pageup, keyNames.home, keyNames.end].includes(event.key)) return;
    const el: HTMLElement = unref(listRef)?.$el;

    if (!el) return;

    if (event.key === keyNames.home || event.key === keyNames.end) {
      el.scrollTo({
        top: event.key === keyNames.home ? 0 : el.scrollHeight,
        behavior: 'smooth'
      });
    }

    await finishScrolling();

    const children = el.querySelectorAll(':scope > :not(.cds-virtual-scroll__spacer)');

    if (event.key === keyNames.pagedown || event.key === keyNames.home) {
      const top = el.getBoundingClientRect().top;

      for (const child of children) {
        if (child.getBoundingClientRect().top >= top) {
          (child as HTMLElement).focus();
          break;
        }
      }
    } else {
      const bottom = el.getBoundingClientRect().bottom;
      for (const child of [...children].reverse()) {
        if (child.getBoundingClientRect().bottom <= bottom) {
          (child as HTMLElement).focus();
          break;
        }
      }
    }
  }

  return { onListScroll, onListKeydown };
}
